import { Box } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  TooltipItem,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { ChartMetric, ChartView, NormalizedPowerRow } from "../types/power";
import {
  buildRoomBarData,
  buildRoomLineData,
  getMetricLabel,
} from "../utils/powerCharts";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
);

interface RoomPowerChartProps {
  rows: NormalizedPowerRow[];
  view: ChartView;
  metric?: ChartMetric;
}

const createBarOptions = (
  metricLabel: string,
  unitLabel: string,
): ChartOptions<"bar"> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        usePointStyle: true,
        boxWidth: 10,
      },
    },
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<"bar">) => {
          const value = context.raw;
          const datasetLabel =
            (context.dataset as { label?: string }).label || "Series";
          if (typeof value === "number") {
            return `${datasetLabel}: ${value.toLocaleString()} ${unitLabel}`;
          }
          return `${datasetLabel}: --`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: "#3b4d5f",
      },
    },
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: metricLabel,
      },
      ticks: {
        color: "#3b4d5f",
      },
      grid: {
        color: "rgba(13, 27, 42, 0.08)",
      },
    },
  },
});

const createLineOptions = (
  metricLabel: string,
  unitLabel: string,
): ChartOptions<"line"> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        usePointStyle: true,
        boxWidth: 10,
      },
    },
    tooltip: {
      callbacks: {
        label: (context: TooltipItem<"line">) => {
          const value = context.raw;
          const datasetLabel =
            (context.dataset as { label?: string }).label || "Series";
          if (typeof value === "number") {
            return `${datasetLabel}: ${value.toLocaleString()} ${unitLabel}`;
          }
          return `${datasetLabel}: --`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        color: "#3b4d5f",
      },
    },
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: metricLabel,
      },
      ticks: {
        color: "#3b4d5f",
      },
      grid: {
        color: "rgba(13, 27, 42, 0.08)",
      },
    },
  },
});

const RoomPowerChart = ({
  rows,
  view,
  metric = "power_active",
}: RoomPowerChartProps) => {
  const metricLabel = getMetricLabel(metric);
  const unitLabel = metric === "total_energy_active" ? "kWh" : "W";
  const barOptions = createBarOptions(metricLabel, unitLabel);
  const lineOptions = createLineOptions(metricLabel, unitLabel);

  return (
    <Box sx={{ height: 360 }}>
      {view === "bar" ? (
        <Bar data={buildRoomBarData(rows, metric)} options={barOptions} />
      ) : (
        <Line data={buildRoomLineData(rows, metric)} options={lineOptions} />
      )}
    </Box>
  );
};

export default RoomPowerChart;
